<script lang="ts">
    import { isValidValue } from 'components/Format';
    import BooleanFormat from 'components/Format/BooleanFormat.svelte';
    import DateFormat from 'components/Format/DateFormat.svelte';
    import GeoFormat from 'components/Format/GeoFormat.svelte';
    import TextFormat from 'components/Format/TextFormat.svelte';
    import NumberFormat from 'components/Format/NumberFormat.svelte';
    import URLFormat from 'components/Format/URLFormat.svelte';
    import { MAX_URL_LENGTH } from 'components/Format/utils';
    import { DATA_FORMAT } from '../constants';
    import { locale, debugWarnings } from '../store';
    import type { Column } from '../types';
    import { isColumnOfType, getOptions, getValue } from './utils';
    import tooltipOnOverflow from '../actions';

    export let record: Record<string, unknown>;
    export let column: Column;

    // Defensive fallback key lookup: only reached when column.dataFormat is outside
    // the DataFormat union, i.e. from an untyped/JS consumer — see the {:else} branch below.
    function getFallbackKey(c: Column): string {
        return (c as unknown as { key: string }).key;
    }

    const shouldDetectUrls = (options: { disableUrlDetection?: boolean } | null) =>
        !options?.disableUrlDetection;
</script>

<div
    class={`cell-content table-data--${column.dataFormat}`}
    use:tooltipOnOverflow={column.dataFormat}
>
    {#if isValidValue(getValue(column, record))}
        {#if isColumnOfType(column, DATA_FORMAT.boolean)}
            <BooleanFormat
                value={getValue(column, record)}
                {...getOptions(column, record)}
                debugWarnings={$debugWarnings}
            />
        {:else if isColumnOfType(column, DATA_FORMAT.date)}
            <DateFormat
                value={getValue(column, record)}
                {...getOptions(column, record)}
                locale={$locale}
                debugWarnings={$debugWarnings}
            />
        {:else if isColumnOfType(column, DATA_FORMAT.geo)}
            <GeoFormat value={getValue(column, record)} {...getOptions(column, record)} />
        {:else if isColumnOfType(column, DATA_FORMAT.shortText)}
            {@const opts = getOptions(column, record)}
            {@const { disableUrlDetection, ...urlFormatOpts } = opts ?? {}}
            {#if shouldDetectUrls(opts)}
                <URLFormat
                    value={getValue(column, record)}
                    {...urlFormatOpts}
                    debugWarnings={$debugWarnings}
                    warnOnInvalidUrl={false}
                    maxLength={MAX_URL_LENGTH}
                />
            {:else}
                <TextFormat
                    value={getValue(column, record)}
                    {...opts}
                    debugWarnings={$debugWarnings}
                />
            {/if}
        {:else if isColumnOfType(column, DATA_FORMAT.longText)}
            {@const opts = getOptions(column, record)}
            {@const { disableUrlDetection, ...urlFormatOpts } = opts ?? {}}
            <span>
                {#if shouldDetectUrls(opts)}
                    <URLFormat
                        value={getValue(column, record)}
                        {...urlFormatOpts}
                        debugWarnings={$debugWarnings}
                        warnOnInvalidUrl={false}
                        maxLength={MAX_URL_LENGTH}
                    />
                {:else}
                    <TextFormat
                        value={getValue(column, record)}
                        {...opts}
                        debugWarnings={$debugWarnings}
                    />
                {/if}
            </span>
        {:else if isColumnOfType(column, DATA_FORMAT.number)}
            <NumberFormat
                value={getValue(column, record)}
                {...getOptions(column, record)}
                locale={$locale}
                debugWarnings={$debugWarnings}
            />
            <!-- eslint-disable-next-line max-len -->
        {:else if isColumnOfType(column, DATA_FORMAT.url) || isColumnOfType(column, DATA_FORMAT.file) || isColumnOfType(column, DATA_FORMAT.image)}
            <URLFormat
                value={getValue(column, record)}
                {...getOptions(column, record)}
                debugWarnings={$debugWarnings}
            />
        {:else if isColumnOfType(column, DATA_FORMAT.json)}
            {@const raw = getValue(column, record)}
            <TextFormat
                value={raw !== null && typeof raw === 'object' ? JSON.stringify(raw) : raw}
                {...getOptions(column, record)}
                debugWarnings={$debugWarnings}
            />
        {:else if isColumnOfType(column, DATA_FORMAT.ipAddress) || isColumnOfType(column, DATA_FORMAT.id)}
            <TextFormat
                value={getValue(column, record)}
                {...getOptions(column, record)}
                debugWarnings={$debugWarnings}
            />
        {:else}
            <!-- Defensive fallback: column.dataFormat can only reach here from an
                untyped/JS consumer passing a value outside the DataFormat union, so
                per-format accessor/options aren't type-safe to apply here. -->
            {@const raw = record[getFallbackKey(column)]}
            <TextFormat
                value={raw !== null && typeof raw === 'object' ? JSON.stringify(raw) : String(raw)}
                debugWarnings={$debugWarnings}
            />
        {/if}
    {/if}
</div>

<style>
    /* Wrapper div to allow position: relative while the <td> has sticky,
        so that the ::after can have position: absolute */
    :global(.ods-dataviz--default .cell-content) {
        position: relative;
        padding: var(--spacing-75);
        overflow: visible;
    }

    :global(.ods-dataviz--default div.table-header--number) {
        text-align: right;
    }
    /* to be improved in the formatting story */
    :global(.ods-dataviz--default div.table-data--long-text > span),
    :global(.ods-dataviz--default div.table-data--short-text),
    :global(.ods-dataviz--default div.table-data--json),
    :global(.ods-dataviz--default div.table-data--ip-address),
    :global(.ods-dataviz--default div.table-data--id),
    :global(.ods-dataviz--default div.table-data--url),
    :global(.ods-dataviz--default div.table-data--file),
    :global(.ods-dataviz--default div.table-data--image) {
        text-overflow: ellipsis;
        overflow: hidden;
        width: max-content;
        min-width: 40px;
        max-width: 240px;
    }
    :global(.ods-dataviz--default div.table-data--long-text > span) {
        display: -webkit-box;
        -webkit-line-clamp: 3;
        line-clamp: 3;
        -webkit-box-orient: vertical;
        white-space: pre-wrap;
    }
    :global(.ods-dataviz--default div.table-data--number) {
        text-align: right;
    }
</style>
